/**
 * Keeps the screen on while the page is open, so the presenters' iPad does
 * not go to sleep during a broadcast. The browser drops the lock whenever the
 * page is hidden, so it is taken again each time the page becomes visible.
 * The system can also drop it while the page is visible, so it is taken again
 * when it is released.
 * Safari can refuse a request made without a user gesture, so every tap also
 * retries while no lock is held. Returns a teardown that releases the lock.
 */
export function keepScreenAwake(): () => void {
  if (!('wakeLock' in navigator)) return () => {};

  let sentinel: WakeLockSentinel | null = null;
  let pending = false;
  let retry = false;
  let stopped = false;

  const request = async () => {
    // An event during a request may mean the page was hidden and shown
    // again, so the request may be refused or its lock dropped; check
    // again once it settles.
    if (pending) {
      retry = true;
      return;
    }
    if (stopped || document.visibilityState !== 'visible') return;
    if (sentinel != null && !sentinel.released) return;
    pending = true;
    try {
      const lock = await navigator.wakeLock.request('screen');
      if (stopped) await lock.release();
      else {
        sentinel = lock;
        lock.addEventListener('release', request);
      }
    } catch {
      // Refused (no gesture yet, low battery mode); the next tap retries.
    } finally {
      pending = false;
    }
    if (retry) {
      retry = false;
      await request();
    }
  };

  document.addEventListener('visibilitychange', request);
  document.addEventListener('pointerup', request, { capture: true });
  request();

  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', request);
    document.removeEventListener('pointerup', request, { capture: true });
    sentinel?.release().catch(() => {});
    sentinel = null;
  };
}
